import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { mdiRefresh, mdiDownload } from '@mdi/js';
import { Icon } from '@mdi/react';

import {
    TableActionBar,
    TableHead,
    TableCell,
    TableSortButton,
    useSelection,
} from 'common/components/Table';
import {
    IPortfolioReportOrderField,
    IContentTypeValue,
    IFeature,
    ExportPortfolioReportDocument,
    CurrentUserDocument,
} from 'graphql/types';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { useApolloError } from 'common/hooks/useApolloError';
import { BoxLoader } from 'common/components/Loader';
import { FilterBar } from 'common/components/FilterBar';
import { Pagination } from 'common/components/Pagination';
import { useReportList } from 'report/hooks/useReportList';
import { Checkbox } from 'common/components/Checkbox';
import { Tooltip } from 'common/components/Tooltip';
import { useFeature } from 'common/hooks/useFeature';
import { IconButton } from 'common/components/IconButton';
import { Button } from 'common/components/Button';
import { ConfirmDialog } from 'common/components/ConfirmDialog';
import { ResetPortfolioItemProgressAction } from 'user/actions/ResetPortfolioItemProgressAction';
import { useFrontendPermissions } from 'user/hooks';

import { CourseReportTableRow } from './CourseReportTableRow';

export const CourseReportTable = () => {
    const [translate] = useTranslation();
    const [displaySnackbar] = useSnackbar();
    const { showApolloError } = useApolloError();
    const [showExportDialog, setShowExportDialog] = useState(false);
    const { canUse: canExport } = useFeature(IFeature.Export);

    const { canResetProgress } = useFrontendPermissions('portfolioItem');

    // Get current user email to use in export dialog
    const { data: currentUserData, loading: loadingCurrentUser } =
        useQuery(CurrentUserDocument);
    const currentUserEmail = currentUserData?.currentUser?.email;

    const relatedObjectTypes = [
        IContentTypeValue.Course,
        IContentTypeValue.EventLti,
        IContentTypeValue.Scorm,
        IContentTypeValue.ExternalContent,
    ];

    const {
        items,
        loading: loadingReport,
        count,
        onSearch,
        onFilterChange,
        filters,
        filterValues,
        onFilterChangeValue,
        currentSort,
        onSort,
        setPage,
        paginationSettings,
        filterValuesForQuery,
    } = useReportList('course', false, {
        relatedObjectTypes,
        withAggregatedFilterValues: true,
        isModuleReport: true,
        isCertificateOrModuleReport: true,
    });

    const loading = loadingReport || loadingCurrentUser;

    const [exportReport, { loading: loadingExport }] = useMutation(
        ExportPortfolioReportDocument,
        {
            variables: {
                relatedObjectTypes,
                ...filterValuesForQuery,
            },
        }
    );

    const { ...selectionProps } = useSelection(items);
    const { rows, selected, onSelectAll, isSelected } = selectionProps;

    const handleExport = async () => {
        try {
            const exportResults = await exportReport();
            const fileUrl = exportResults.data?.exportPortfolioReport?.fileUrl;

            if (fileUrl) {
                window.open(fileUrl, '_blank');
            } else {
                displaySnackbar(translate('reportPage.exportSuccess'), {
                    variant: 'success',
                });
            }
        } catch (error) {
            showApolloError(error);

            return;
        }

        setShowExportDialog(false);
    };

    const handleSearch = (searchValue: string) => {
        setPage(1);
        onSearch?.(searchValue);
    };

    const tableActions = canResetProgress && (
        <ResetPortfolioItemProgressAction
            portfolioItemIds={selected}
            onSuccess={() => {
                // Deselect all
                !!selected.length && onSelectAll();
            }}
        >
            <Tooltip title={translate('resetProgress')}>
                <IconButton
                    aria-label={translate('resetProgress')}
                    disabled={!isSelected}
                    iconPath={mdiRefresh}
                    iconSize="2.4rem"
                />
            </Tooltip>
        </ResetPortfolioItemProgressAction>
    );

    return (
        <>
            <FilterBar
                useFilterDrawer
                barAppend={
                    canExport && (
                        <Button
                            disabled={count === 0}
                            startIcon={
                                <Icon path={mdiDownload} size="1.9rem" />
                            }
                            sx={{ flex: 1 }}
                            variant="outlined"
                            onClick={() => setShowExportDialog(true)}
                        />
                    )
                }
                filterDrawerProps={{
                    title: translate('reportPage.filterFormCourse.title'),
                    description: translate(
                        'reportPage.filterFormCourse.description'
                    ),
                    formProps: {
                        onReset: (values) => {
                            setPage(1);
                            onFilterChange(values);
                        },
                    },
                }}
                formFilters={filters}
                formFilterValues={filterValues}
                initialSearchValue={filterValues.q}
                placeholder={translate('filterBarPlaceholder')}
                onChangeField={onFilterChangeValue}
                onFiltersChange={(values) => {
                    setPage(1);
                    onFilterChange(values);
                }}
                onSearch={handleSearch}
                onSearchClear={() => handleSearch('')}
            />

            {!loading && !!rows.length && (
                <TableContainer>
                    {tableActions && (
                        <TableActionBar
                            actions={tableActions}
                            selectionProps={selectionProps}
                        />
                    )}

                    <Table>
                        <TableHead>
                            <TableRow>
                                {canResetProgress && (
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={false}
                                            disabled={isSelected}
                                            onChange={onSelectAll}
                                        />
                                    </TableCell>
                                )}
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    <Box sx={{ display: 'flex' }}>
                                        {translate('user')}
                                        <TableSortButton
                                            currentSortDirection={
                                                currentSort.direction
                                            }
                                            currentSortField={
                                                currentSort.field as string
                                            }
                                            sortField={
                                                IPortfolioReportOrderField.UserName
                                            }
                                            onClick={() =>
                                                onSort(
                                                    IPortfolioReportOrderField.UserName
                                                )
                                            }
                                        />
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    <Box sx={{ display: 'flex' }}>
                                        {translate('item')}
                                        <TableSortButton
                                            currentSortDirection={
                                                currentSort.direction
                                            }
                                            currentSortField={
                                                currentSort.field as string
                                            }
                                            sortField={
                                                IPortfolioReportOrderField.Title
                                            }
                                            onClick={() =>
                                                onSort(
                                                    IPortfolioReportOrderField.Title
                                                )
                                            }
                                        />
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    <Box sx={{ display: 'flex' }}>
                                        {translate('start')}
                                        <TableSortButton
                                            currentSortDirection={
                                                currentSort.direction
                                            }
                                            currentSortField={
                                                currentSort.field as string
                                            }
                                            sortField={
                                                IPortfolioReportOrderField.StartDate
                                            }
                                            onClick={() =>
                                                onSort(
                                                    IPortfolioReportOrderField.StartDate
                                                )
                                            }
                                        />
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    <Box sx={{ display: 'flex' }}>
                                        {translate('fulfilledOn')}
                                        <TableSortButton
                                            currentSortDirection={
                                                currentSort.direction
                                            }
                                            currentSortField={
                                                currentSort.field as string
                                            }
                                            sortField={
                                                IPortfolioReportOrderField.CompletionDate
                                            }
                                            onClick={() =>
                                                onSort(
                                                    IPortfolioReportOrderField.CompletionDate
                                                )
                                            }
                                        />
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    <Box sx={{ display: 'flex' }}>
                                        {translate('time')}
                                        <TableSortButton
                                            currentSortDirection={
                                                currentSort.direction
                                            }
                                            currentSortField={
                                                currentSort.field as string
                                            }
                                            sortField={
                                                IPortfolioReportOrderField.TimeSpent
                                            }
                                            onClick={() =>
                                                onSort(
                                                    IPortfolioReportOrderField.TimeSpent
                                                )
                                            }
                                        />
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    <Box sx={{ display: 'flex' }}>
                                        {translate('score')}
                                        <TableSortButton
                                            currentSortDirection={
                                                currentSort.direction
                                            }
                                            currentSortField={
                                                currentSort.field as string
                                            }
                                            sortField={
                                                IPortfolioReportOrderField.Score
                                            }
                                            onClick={() =>
                                                onSort(
                                                    IPortfolioReportOrderField.Score
                                                )
                                            }
                                        />
                                    </Box>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows?.map(({ index, onSelect, isSelected }) => {
                                const reportItem = items[index];

                                if (!reportItem) return null;

                                return (
                                    <CourseReportTableRow
                                        hideCheckbox={!canResetProgress}
                                        isSelected={isSelected}
                                        key={reportItem.id}
                                        reportItem={reportItem}
                                        onSelect={onSelect}
                                    />
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {loading && <BoxLoader />}

            {!loading && !items.length && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Typography>
                        {translate('noOptionsText.reportItems')}
                    </Typography>
                </Box>
            )}

            {!loading && (
                <Box sx={{ mt: 4 }}>
                    <Pagination
                        page={paginationSettings.page}
                        pageAmount={paginationSettings.pageAmount}
                        totalsAmount={paginationSettings.count}
                        totalsText={translate('portfolioReportItems', {
                            count,
                        })}
                        onChange={(page: number) => {
                            setPage(page);
                        }}
                    />
                </Box>
            )}

            <ConfirmDialog
                confirmText={translate('export')}
                loading={loadingExport}
                open={!!showExportDialog}
                title={translate('reportPage.exportCourseDialog.title')}
                onCancel={() => setShowExportDialog(false)}
                onClose={() => setShowExportDialog(false)}
                onConfirm={handleExport}
            >
                {translate(`reportPage.exportCourseDialog.text`, {
                    email: currentUserEmail,
                })}
            </ConfirmDialog>
        </>
    );
};
